import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import AnalysisBuilder from "../components/AnalysisBuilder";
import { ReportingLayoutProps } from "./ReportingLayout";
import { AnalysisQuery, Dashboard, SavedAnalysis } from "../types/analytics";
import { POST_SAVE_ACTIVE_DASHBOARD_KEY } from "../lib/reportingSession";

const layoutForIndex = (analysis: SavedAnalysis, index: number) => ({
  ...analysis,
  layout: {
    i: analysis.id,
    x: (index % 2) * 6,
    y: Math.floor(index / 2) * 4,
    w: 6,
    h: 4,
    minW: 3,
    minH: 3,
  },
});

const AnalysisBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { analysisId } = useParams<{ analysisId?: string }>();
  const {
    dataService,
    onAnalysesUpdate,
    onDashboardsUpdate,
    transactions,
    gradingOrders,
    savedAnalyses,
  } = useOutletContext<ReportingLayoutProps>();

  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  const mode = analysisId ? "edit" : "create";
  const initialAnalysis = useMemo(
    () => (analysisId ? savedAnalyses.find((a) => a.id === analysisId) ?? null : null),
    [analysisId, savedAnalyses]
  );

  const handleSave = useCallback(
    async (analysis: AnalysisQuery) => {
      if (!dataService) return;
      setValidationMessage(null);

      try {
        if (mode === "edit" && initialAnalysis) {
          await dataService.updateSavedAnalysis(initialAnalysis.id, analysis);
        } else {
          const saved: SavedAnalysis = {
            ...analysis,
            description: "",
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
          };
          await dataService.saveAnalysis(saved);
        }

        const updated = await dataService.getSavedAnalyses();
        onAnalysesUpdate(updated);

        const postDashboardId =
          typeof window !== "undefined" ? sessionStorage.getItem(POST_SAVE_ACTIVE_DASHBOARD_KEY) : null;
        if (typeof window !== "undefined") {
          sessionStorage.removeItem(POST_SAVE_ACTIVE_DASHBOARD_KEY);
        }

        if (postDashboardId && mode === "create" && onDashboardsUpdate) {
          const created =
            updated.find((a: SavedAnalysis) => a.id === analysis.id) ||
            updated.find((a: SavedAnalysis) => a.name === analysis.name);
          if (created) {
            const dashboards = await dataService.getDashboards();
            const target = dashboards.find((d: Dashboard) => d.id === postDashboardId);
            if (target && !target.analyses.some((a: SavedAnalysis) => a.id === created.id)) {
              const nextAnalyses = [...target.analyses, layoutForIndex(created, target.analyses.length)];
              await dataService.updateDashboard(target.id, { analyses: nextAnalyses });
              onDashboardsUpdate(await dataService.getDashboards());
            }
          }
        }

        navigate("/reporting", { replace: true });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to save analysis";
        setValidationMessage(msg);
        throw e;
      }
    },
    [dataService, mode, initialAnalysis, onAnalysesUpdate, onDashboardsUpdate, navigate]
  );

  const handleCancel = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(POST_SAVE_ACTIVE_DASHBOARD_KEY);
    }
    navigate("/reporting");
  }, [navigate]);

  if (mode === "edit" && analysisId && !initialAnalysis) {
    return (
      <div className="container py-12 space-y-4">
        <p className="text-muted-foreground">Analysis not found.</p>
        <button type="button" className="text-primary underline" onClick={handleCancel}>
          Back to reporting
        </button>
      </div>
    );
  }

  return (
    <div className="container py-8 space-y-4">
      {validationMessage && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {validationMessage}
        </div>
      )}
      <AnalysisBuilder
        open
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        variant="page"
        onCancel={handleCancel}
        onSave={handleSave}
        transactions={transactions}
        gradingOrders={gradingOrders}
        initialAnalysis={initialAnalysis}
        mode={mode}
        onValidationError={(msg) => setValidationMessage(msg)}
      />
    </div>
  );
};

export default AnalysisBuilderPage;
